(() => {
  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr');

  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`french_bites:${name}`, { detail }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `french_bites_${name}`, ...detail });
    }
  };

  const initialize = (root) => {
    let data;
    try {
      const rows = JSON.parse(root.querySelector('[data-fmm-data]')?.textContent || '[]');
      data = rows.map((row, index) => ({
        position: index + 1,
        french: row[0],
        category: row[1],
        level: row[2],
        meaning1: row[3],
        example1Fr: row[4],
        example1En: row[5],
        meaning2: row[6],
        example2Fr: row[7],
        example2En: row[8],
        audioReference: row[9],
      }));
    } catch (error) {
      console.error('French Bites multiple-meaning data could not be loaded.', error);
      data = [];
    }

    const isMember = root.dataset.member === 'true';
    const audioEnabled = root.dataset.audioEnabled === 'true';
    const audioPath = root.dataset.audioPath || '';
    const pageSize = Math.max(25, Number.parseInt(root.dataset.pageSize || '25', 10));
    const list = root.querySelector('[data-fmm-list]');
    const search = root.querySelector('[data-fmm-search]');
    const count = root.querySelector('[data-fmm-count]');
    const previous = root.querySelector('[data-fmm-previous]');
    const next = root.querySelector('[data-fmm-next]');
    const pageStatus = root.querySelector('[data-fmm-page-status]');
    const empty = root.querySelector('[data-fmm-empty]');
    const liveStatus = root.querySelector('[data-fmm-live-status]');
    if (!list || !search || !count || !previous || !next || !pageStatus || !empty) return;

    let page = 1;
    let query = '';
    let currentAudio = null;
    let currentButton = null;
    let debounceTimer = null;

    const stopAudio = () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      if (currentButton) {
        currentButton.setAttribute('aria-busy', 'false');
        currentButton.removeAttribute('aria-pressed');
      }
      currentAudio = null;
      currentButton = null;
    };

    const playAudio = async (item, button) => {
      stopAudio();
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-pressed', 'true');
      currentButton = button;
      const url = /^https:\/\//i.test(item.audioReference)
        ? item.audioReference
        : `${audioPath}${encodeURIComponent(item.audioReference)}`;
      currentAudio = new Audio(url);
      currentAudio.preload = 'none';
      currentAudio.addEventListener('playing', () => {
        button.setAttribute('aria-busy', 'false');
        if (liveStatus) liveStatus.textContent = `Playing ${item.french}`;
        track('multiple_meanings_pronunciation_played', { position: item.position, member: isMember });
      }, { once: true });
      currentAudio.addEventListener('ended', stopAudio, { once: true });
      currentAudio.addEventListener('error', () => {
        stopAudio();
        if (liveStatus) liveStatus.textContent = `Pronunciation for ${item.french} is temporarily unavailable.`;
      }, { once: true });
      try {
        await currentAudio.play();
      } catch (_error) {
        stopAudio();
        if (liveStatus) liveStatus.textContent = `Pronunciation for ${item.french} could not be played.`;
      }
    };

    const makeSense = (meaning, frenchExample, englishExample) => {
      const wrapper = document.createElement('p');
      wrapper.className = 'fba__sense';
      const label = document.createElement('span');
      label.className = 'fba__sense-label';
      label.textContent = meaning;
      const french = document.createElement('span');
      french.className = 'fba__example';
      french.lang = 'fr';
      french.textContent = frenchExample;
      const english = document.createElement('span');
      english.className = 'fba__example';
      english.textContent = englishExample;
      wrapper.append(label, french, english);
      return wrapper;
    };

    const makeRow = (item) => {
      const row = document.createElement('li');
      row.className = 'fba__row';
      const position = document.createElement('span');
      position.className = 'fba__position';
      position.textContent = String(item.position).padStart(4, '0');
      const heading = document.createElement('div');
      const word = document.createElement('strong');
      word.className = 'fba__french';
      word.lang = 'fr';
      word.textContent = item.french;
      const meta = document.createElement('small');
      meta.className = 'fba__meta';
      meta.textContent = [item.level, item.category].filter(Boolean).join(' · ');
      heading.append(word, meta);
      const meanings = document.createElement('div');
      meanings.className = 'fba__translation';
      meanings.append(
        makeSense(item.meaning1, item.example1Fr, item.example1En),
        makeSense(item.meaning2, item.example2Fr, item.example2En),
      );
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fba__audio button button--secondary';
      button.setAttribute('aria-label', `Listen to the pronunciation of ${item.french}`);
      button.setAttribute('aria-busy', 'false');
      button.disabled = !audioEnabled || (!audioPath && !/^https:\/\//i.test(item.audioReference));
      const symbol = document.createElement('span');
      symbol.className = 'fba__audio-symbol';
      symbol.setAttribute('aria-hidden', 'true');
      symbol.textContent = '▶';
      button.append(symbol);
      if (!button.disabled) button.addEventListener('click', () => playAudio(item, button));
      row.append(position, heading, meanings, button);
      return row;
    };

    const render = () => {
      stopAudio();
      const filtered = query
        ? data.filter((item) => normalize([
          item.french, item.category, item.meaning1, item.example1Fr, item.example1En,
          item.meaning2, item.example2Fr, item.example2En,
        ].join(' ')).includes(query))
        : data;
      page = Math.min(page, Math.max(1, Math.ceil(filtered.length / pageSize)));
      const start = (page - 1) * pageSize;
      const visible = filtered.slice(start, start + pageSize);
      list.replaceChildren(...visible.map(makeRow));
      empty.hidden = filtered.length !== 0;
      count.textContent = filtered.length === data.length
        ? `${data.length} words available`
        : `${filtered.length} matching words`;
      pageStatus.textContent = filtered.length
        ? `Showing ${start + 1}–${start + visible.length} of ${filtered.length}`
        : 'No results';
      previous.disabled = page <= 1;
      next.disabled = page >= Math.ceil(filtered.length / pageSize);
    };

    search.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        query = normalize(search.value.trim());
        page = 1;
        render();
        track('multiple_meanings_search', { has_query: Boolean(query), member: isMember });
      }, 150);
    });
    previous.addEventListener('click', () => {
      page -= 1;
      render();
      root.querySelector('[data-fmm-list-heading]')?.focus();
    });
    next.addEventListener('click', () => {
      page += 1;
      render();
      root.querySelector('[data-fmm-list-heading]')?.focus();
    });
    root.querySelector('[data-fmm-membership-cta]')?.addEventListener('click', () => {
      track('membership_cta_clicked', { module: 'multiple_meanings' });
    });
    render();
    track('multiple_meanings_page_view', { member: isMember, available_words: data.length });
  };

  document.querySelectorAll('[data-fmm-root]').forEach(initialize);
})();
